/* RecapProductQuantities: in the "Create Trade Recap" pop-up of a Bid/Offer
 * with several products, how much of each product this recap takes. Each box starts at what is still free of that product; the trader
 * may lower any of them (the buyer takes all of the first coal and half of
 * the second). The bid closes on its total, as before (Chris's call,
 * 2026-10-20): the pop-up refuses only a recap of nothing or one beyond what
 * is left on the bid.
 */
import React from 'react';
import AmountInput from './ui/AmountInput';
import { QUANTITY_DECIMALS, addFigures, formatFigure } from '../utils/precision';
import './DealProductLines.css';

export const recapQuantitiesTotal = (quantities) => (
  (quantities || []).reduce(
    (sum, q) => addFigures(sum, String(q || '').replace(/[\s,]/g, '') || '0', QUANTITY_DECIMALS),
    '0',
  )
);

// The pop-up's refusal, in screen words, or '' when the recap may be made.
export const recapQuantitiesProblem = (quantities, bidRemaining, formatQuantity) => {
  const total = parseFloat(recapQuantitiesTotal(quantities));
  if (!(total > 0)) return 'Enter a quantity for at least one product';
  if (total > bidRemaining) return `Together they cannot exceed ${formatQuantity(bidRemaining)}`;
  return '';
};

const RecapProductQuantities = ({
  lines,
  free,
  quantities,
  onChange,
  unit = '',
  error = '',
  bidRemaining,
  formatQuantity,
}) => {
  const total = recapQuantitiesTotal(quantities);
  const left = Math.max(0, bidRemaining - (parseFloat(total) || 0));
  return (
    <div className="hms-deal-lines">
      <table className="hms-deal-lines__table">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col" className="hms-deal-lines__num">{`Free on the bid${unit ? ` (${unit})` : ''}`}</th>
            <th scope="col" className="hms-deal-lines__num">{`This recap${unit ? ` (${unit})` : ''}`}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id ?? index}>
              <td>{line.product_label || `Product ${index + 1}`}</td>
              <td className="hms-deal-lines__num">{formatQuantity(parseFloat(free[index]) || 0)}</td>
              <td className="hms-deal-lines__num">
                <AmountInput
                  className="hms-input"
                  aria-label={`${line.product_label || `Product ${index + 1}`} for this recap`}
                  value={quantities[index] ?? ''}
                  maxDecimals={QUANTITY_DECIMALS}
                  onChange={(value) => onChange(quantities.map((q, i) => (i === index ? value : q)))}
                  placeholder="0"
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>This recap in total</th>
            <td className="hms-deal-lines__num">{formatFigure(total, 0, QUANTITY_DECIMALS)}</td>
          </tr>
        </tfoot>
      </table>
      {error ? (
        <div className="hms-deal-lines-error" role="alert">{error}</div>
      ) : (
        <div className="hms-deal-lines-derived-hint">
          The bid will stay open with <strong>{formatQuantity(left)}</strong> available after this recap.
        </div>
      )}
    </div>
  );
};

export default RecapProductQuantities;
